import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { logger } from '../lib/utils/logger';
import { useNavigate, matchPath } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../lib/hooks/useAuth';
import { TAB_REGISTRY, DYNAMIC_ROUTES } from '../constants/tabRegistry';

const TabContext = createContext(null);

const ROLE_LEVEL = { USER: 1, TENANT_ADMIN: 2, GLOBAL_ADMIN: 3 };
const MAX_TABS = 10;
const STORAGE_KEY = 'app_tabs';

// ============================================
// Helpers
// ============================================

function findRegistryEntry(path) {
  if (TAB_REGISTRY[path]) return TAB_REGISTRY[path];
  for (const route of DYNAMIC_ROUTES) {
    if (matchPath(route.pattern, path)) return route;
  }
  return null;
}

// ============================================
// Reducer
// ============================================

const initialState = {
  tabs: [],
  activeTabId: null,
  showHome: true,
  initialized: false,
  tabMeta: {}, // Per-tab metadata: { [tabId]: { key: value } }
};

function tabReducer(state, action) {
  switch (action.type) {
    case 'INIT':
      return { ...action.payload, initialized: true };

    case 'OPEN_TAB': {
      const { tab } = action.payload;
      return {
        ...state,
        tabs: [...state.tabs, tab],
        activeTabId: tab.id,
        showHome: false,
      };
    }

    case 'ACTIVATE_TAB':
      return { ...state, activeTabId: action.payload.tabId, showHome: false };

    case 'CLOSE_TAB': {
      const { tabId } = action.payload;
      const idx = state.tabs.findIndex((t) => t.id === tabId);
      if (idx === -1) return state;

      const newTabs = state.tabs.filter((t) => t.id !== tabId);
      const { [tabId]: _removed, ...remainingMeta } = state.tabMeta;

      // Closing a non-active tab
      if (tabId !== state.activeTabId) {
        return { ...state, tabs: newTabs, tabMeta: remainingMeta };
      }

      // Closing the active tab — pick adjacent
      if (newTabs.length === 0) {
        return {
          ...state,
          tabs: newTabs,
          activeTabId: null,
          showHome: true,
          tabMeta: remainingMeta,
        };
      }
      const newIdx = Math.min(idx, newTabs.length - 1);
      return {
        ...state,
        tabs: newTabs,
        activeTabId: newTabs[newIdx].id,
        showHome: false,
        tabMeta: remainingMeta,
      };
    }

    case 'CLOSE_OTHER_TABS': {
      const { tabId } = action.payload;
      const keepTab = state.tabs.find((t) => t.id === tabId);
      if (!keepTab) return state;
      return {
        ...state,
        tabs: [keepTab],
        activeTabId: keepTab.id,
        showHome: false,
      };
    }

    case 'CLOSE_TABS_TO_RIGHT': {
      const { tabId } = action.payload;
      const idx = state.tabs.findIndex((t) => t.id === tabId);
      if (idx === -1) return state;
      const newTabs = state.tabs.slice(0, idx + 1);
      const activeStillExists = newTabs.find((t) => t.id === state.activeTabId);
      return {
        ...state,
        tabs: newTabs,
        activeTabId: activeStillExists ? state.activeTabId : newTabs[newTabs.length - 1].id,
        showHome: false,
      };
    }

    case 'CLOSE_ALL_TABS':
      return { ...state, tabs: [], activeTabId: null, showHome: true, tabMeta: {} };

    case 'GO_HOME':
      return { ...state, activeTabId: null, showHome: true };

    case 'SET_TAB_META': {
      const { tabId, key, value } = action.payload;
      return {
        ...state,
        tabMeta: {
          ...state.tabMeta,
          [tabId]: { ...(state.tabMeta[tabId] || {}), [key]: value },
        },
      };
    }

    default:
      return state;
  }
}

// ============================================
// Provider
// ============================================

export function TabProvider({ children }) {
  const [state, dispatch] = useReducer(tabReducer, initialState);
  const { user } = useAuth();
  const navigate = useNavigate();
  const initRef = useRef(false);

  // --- Initialization (once) ---
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const currentPath = window.location.pathname;
    let initPayload = { tabs: [], activeTabId: null, showHome: true, tabMeta: {} };

    // Restore from sessionStorage
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const {
          tabs: savedTabs,
          activeTabId: savedActiveId,
          tabMeta: savedMeta,
        } = JSON.parse(saved);
        if (savedTabs && savedTabs.length > 0) {
          initPayload.tabs = savedTabs;
          initPayload.tabMeta = savedMeta || {};

          const matchingTab = savedTabs.find((t) => t.path === currentPath);
          if (matchingTab) {
            initPayload.activeTabId = matchingTab.id;
            initPayload.showHome = false;
          } else if (currentPath === '/' || currentPath === '') {
            initPayload.showHome = true;
          } else {
            // URL is a new path not in saved tabs — try to open it
            const entry = findRegistryEntry(currentPath);
            if (entry && savedTabs.length < MAX_TABS) {
              const newTab = { id: currentPath, path: currentPath, label: entry.label };
              initPayload.tabs = [...savedTabs, newTab];
              initPayload.activeTabId = newTab.id;
              initPayload.showHome = false;
            } else {
              // Unknown path — open a 404 tab
              const notFoundTab = { id: currentPath, path: currentPath, label: '404' };
              initPayload.tabs = [...savedTabs, notFoundTab];
              initPayload.activeTabId = notFoundTab.id;
              initPayload.showHome = false;
            }
          }

          dispatch({ type: 'INIT', payload: initPayload });
          return;
        }
      }
    } catch (e) {
      // ignore parse errors
    }

    // No saved tabs — open tab for current URL if applicable
    if (currentPath && currentPath !== '/') {
      const entry = findRegistryEntry(currentPath);
      const label = entry ? entry.label : '404';
      const tab = { id: currentPath, path: currentPath, label };
      initPayload = { tabs: [tab], activeTabId: tab.id, showHome: false };
    }

    dispatch({ type: 'INIT', payload: initPayload });
  }, []);

  // --- Persist to sessionStorage ---
  useEffect(() => {
    if (!state.initialized) return;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        tabMeta: state.tabMeta,
      })
    );
  }, [state.tabs, state.activeTabId, state.tabMeta, state.initialized]);

  // --- Sync URL via React Router navigate ---
  // Push to history so browser back button works within the SPA.
  // Initial navigation uses replace to avoid duplicate entry.
  const isInitialNav = useRef(true);
  const lastNavigatedPath = useRef(window.location.pathname);

  useEffect(() => {
    if (!state.initialized) return;
    const targetPath = state.showHome ? '/' : state.activeTabId;
    if (!targetPath || targetPath === lastNavigatedPath.current) return;

    lastNavigatedPath.current = targetPath;

    if (isInitialNav.current) {
      isInitialNav.current = false;
      navigate(targetPath, { replace: true });
    } else {
      navigate(targetPath);
    }
  }, [state.showHome, state.activeTabId, state.initialized, navigate]);

  // --- Handle browser back/forward button ---
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      lastNavigatedPath.current = path;

      if (path === '/' || path === '') {
        dispatch({ type: 'GO_HOME' });
      } else {
        const existingTab = state.tabs.find((t) => t.path === path);
        if (existingTab) {
          dispatch({ type: 'ACTIVATE_TAB', payload: { tabId: existingTab.id } });
        } else {
          // Path has no open tab — try to open it or go home
          const entry = findRegistryEntry(path);
          if (entry && state.tabs.length < MAX_TABS) {
            const tab = { id: path, path, label: entry.label };
            dispatch({ type: 'OPEN_TAB', payload: { tab } });
          } else {
            dispatch({ type: 'GO_HOME' });
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [state.tabs]);

  // --- Actions ---
  const openTab = useCallback(
    (path, label) => {
      // Duplicate check
      const existing = state.tabs.find((t) => t.path === path);
      if (existing) {
        dispatch({ type: 'ACTIVATE_TAB', payload: { tabId: existing.id } });
        return;
      }

      // Max tabs
      if (state.tabs.length >= MAX_TABS) {
        toast.warning(`최대 ${MAX_TABS}개까지 열 수 있습니다. 탭을 닫고 다시 시도해주세요.`);
        return;
      }

      // Resolve entry
      const entry = findRegistryEntry(path);
      if (!entry) {
        logger.warn(`No registry entry for path: ${path}`);
        return;
      }

      // Role check
      const userRole = user?.role || 'USER';
      if (entry.minRole && (ROLE_LEVEL[userRole] || 0) < (ROLE_LEVEL[entry.minRole] || 0)) {
        toast.error('접근 권한이 없습니다.');
        return;
      }

      const tab = { id: path, path, label: label || entry.label };
      dispatch({ type: 'OPEN_TAB', payload: { tab } });
    },
    [state.tabs, user]
  );

  const closeTab = useCallback((tabId) => {
    dispatch({ type: 'CLOSE_TAB', payload: { tabId } });
  }, []);

  const activateTab = useCallback((tabId) => {
    dispatch({ type: 'ACTIVATE_TAB', payload: { tabId } });
  }, []);

  const closeOtherTabs = useCallback((tabId) => {
    dispatch({ type: 'CLOSE_OTHER_TABS', payload: { tabId } });
  }, []);

  const closeTabsToRight = useCallback((tabId) => {
    dispatch({ type: 'CLOSE_TABS_TO_RIGHT', payload: { tabId } });
  }, []);

  const closeAllTabs = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_TABS' });
  }, []);

  const goHome = useCallback(() => {
    dispatch({ type: 'GO_HOME' });
  }, []);

  const setTabMeta = useCallback((tabId, key, value) => {
    dispatch({ type: 'SET_TAB_META', payload: { tabId, key, value } });
  }, []);

  const getTabMeta = useCallback(
    (tabId, key) => {
      return state.tabMeta[tabId]?.[key] ?? null;
    },
    [state.tabMeta]
  );

  const value = {
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    showHome: state.showHome,
    tabMeta: state.tabMeta,
    openTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    closeAllTabs,
    activateTab,
    goHome,
    setTabMeta,
    getTabMeta,
  };

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}

export function useTab() {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within a TabProvider');
  }
  return context;
}

/**
 * useTabActivate — calls `callback` whenever the given tab path becomes active.
 *
 * Usage in a page component:
 *   useTabActivate('/workflow-editor', () => reactFlowInstance.fitView());
 */
export function useTabActivate(tabPath, callback) {
  const { activeTabId, showHome } = useTab();
  const prevActiveRef = useRef(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const wasActive = prevActiveRef.current === tabPath && !showHome;
    const isActive = activeTabId === tabPath && !showHome;
    prevActiveRef.current = activeTabId;

    // Fire only on transition: inactive → active
    if (isActive && !wasActive) {
      // Delay slightly to let display:block take effect before measuring layout
      const timer = setTimeout(() => callbackRef.current(), 50);
      return () => clearTimeout(timer);
    }
  }, [activeTabId, showHome, tabPath]);
}

/**
 * useIsTabActive — returns whether the given tab path is currently active.
 *
 * Useful for pausing SSE connections or polling when a tab is hidden.
 * Usage:
 *   const isActive = useIsTabActive('/admin');
 *   useAdminSSE({ endpoint, enabled: isActive });
 */
export function useIsTabActive(tabPath) {
  const { activeTabId, showHome } = useTab();
  return activeTabId === tabPath && !showHome;
}
